import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { uniq } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import onHoverPopup from 'webapp/modifiers/on-hover-popup';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ForecastsDataCorrelationBadgeComponent from 'webapp/components/forecasts/data/correlation-badge';
import ForecastsPopupsCorrelationFailureComponent from 'webapp/components/forecasts/popups/correlation-failure';
import ForecastsSettingsRegressorMethodologyComponent from 'webapp/components/forecasts/settings/regressor-methodology';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import LoaderSvgComponent from 'webapp/components/loader-svg';
import type { TrackedRequestState } from 'webapp/components/metric-details/forecasts/explorer';
import ScrollableTextComponent from 'webapp/components/scrollable-text';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import SourcePopupResolveComponent from 'webapp/components/source/popup-resolve';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { ForecastMethodology } from 'webapp/models-custom/forecast';
import Forecast from 'webapp/models-custom/forecast';
import RegressorMetric from 'webapp/models-custom/regressor-metric';

import type { onConnectSourceResult } from 'webapp/services/connect';

export enum RegressorAction {
	OPEN_METRIC_INSIGHTS = 'openMetricInsights',
	REMOVE_REGRESSOR = 'removeRegressor',
}

interface Args {
	model: Forecast | RegressorMetric;
	forecast: Forecast;
	mainForecastRequestState: TrackedRequestState;
	isViewingHistoricalSnapshot: boolean;
	onRegressorChange: (regressor: RegressorMetric) => void;
	onRegressorRemove: (regressor: RegressorMetric) => void;
}

export default class TableRowMetricCellComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/forecasts') declare mmForecastsService: Service.ModelManager.Forecasts;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('metric-details')
	declare metricDetailsService: Service.MetricDetails;

	get isForecastRow(): boolean {
		return this.args.model instanceof Forecast;
	}

	get isRegressorRow(): boolean {
		return this.args.model instanceof RegressorMetric;
	}

	get isRegressorEnabled(): boolean | undefined {
		return this.args.model instanceof RegressorMetric ? this.args.model.enabled : undefined;
	}

	get isLoading(): boolean {
		// show loading spinner when loading (both initial and refresh)
		if (this.isForecastRow) {
			const mainForecastLoading = this.args.mainForecastRequestState?.isLoading ?? false,
				forecast = this.args.model as Forecast,
				hasData = !!forecast.tableBlock;

			return mainForecastLoading && hasData;
		} else {
			const regressor = this.args.model as RegressorMetric;
			// show spinner during both initial load and refresh
			return regressor.isLoading;
		}
	}

	// disable all controls when viewing historical snapshot or when loading
	get isDisabled(): boolean {
		if (
			this.args.isViewingHistoricalSnapshot ||
			this.hasNoMetricPermission ||
			this.hasMetricUnavailableError ||
			this.needsConnection ||
			this.isLoading ||
			this.args.mainForecastRequestState?.isLoading
		) {
			return true;
		}

		if (this.isRegressorRow) {
			const regressor = this.args.model as RegressorMetric;
			if (regressor.isLoading) {
				return true;
			}
		}

		return false;
	}

	get isRegressorDisabled(): boolean {
		if (this.isRegressorRow) {
			return !(this.args.model as RegressorMetric).enabled;
		}

		return false;
	}

	get showError(): boolean {
		if (!this.args.model) {
			return false;
		}

		if (this.isForecastRow) {
			return !!this.args.mainForecastRequestState?.error;
		} else {
			return !!(this.args.model as RegressorMetric).error || this.hasMetricUnavailableError;
		}
	}

	get hasCorrelationCalculationFailed(): boolean {
		if (this.isRegressorRow) {
			return (this.args.model as RegressorMetric).correlationCalculationFailed;
		}
		return false;
	}

	get attentionMessage(): string | undefined {
		if (this.hasMetricUnavailableError) {
			return 'Metric not available';
		}

		if (this.hasNoMetricPermission) {
			return 'You do not have permission to access/view this metric.';
		}

		if (this.isForecastRow) {
			const error = this.args.mainForecastRequestState?.error;
			return (error instanceof Error && error.message) || undefined; // a RequestError has no message
		} else {
			return (this.args.model as RegressorMetric).error?.message;
		}
	}

	get showAttentionIcon(): boolean {
		if (this.showConnectButton) {
			return false;
		}
		return !!this.attentionMessage;
	}

	get metricName(): string {
		return this.args.model.displayMetricName;
	}

	get hasNoMetricPermission(): boolean {
		return this.args.model.hasNoMetricPermission;
	}

	get hasMetricUnavailableError(): boolean {
		if (this.isRegressorRow) {
			return (this.args.model as RegressorMetric).hasMetricUnavailableError;
		}
		return false;
	}

	get metricIcon(): string | undefined {
		return this.args.model.metricSetup?.metric?.sourceAvatar;
	}

	get correlationValue(): number | null {
		if (this.isRegressorRow) {
			return (this.args.model as RegressorMetric).correlation;
		}

		return null;
	}

	get isCalculatingCorrelation(): boolean {
		if (this.isRegressorRow) {
			return (this.args.model as RegressorMetric).isCalculatingCorrelation;
		}
		return false;
	}

	@action
	openRegressorOptions(event: Event): void {
		const target = event.target as HTMLElement;
		const attachTo = target;

		const itemsList: PopupListItem<RegressorAction>[] = [
			{
				action: RegressorAction.OPEN_METRIC_INSIGHTS,
				text: 'Open in Metric insights',
				icon: 'MetricDetail',
			},
		];

		if (!this.args.isViewingHistoricalSnapshot) {
			itemsList.push({
				action: RegressorAction.REMOVE_REGRESSOR,
				text: 'Remove metric',
				icon: 'Close',
				class: 'delete',
			});
		}

		const popup = this.popupService.createListPopup(
			attachTo,
			'',
			'popup-type-regressor-options',
		);

		if (popup) {
			popup.open({
				itemsList,
				doAction: (actionName?: string) => {
					void popup.close();
					this.handleAction(actionName as RegressorAction);
				},
			});
		}
	}

	@action
	changeMethodology(methodology: ForecastMethodology): void {
		if (this.isRegressorRow) {
			const regressor = this.args.model as RegressorMetric;

			regressor.methodology = methodology;

			this.args.onRegressorChange(regressor);
		}
	}

	@action
	handleCorrelationChange(correlationValue: number): void {
		if (this.isRegressorRow) {
			const regressor = this.args.model as RegressorMetric;

			// clear the failure flag when user manually sets correlation
			regressor.setCalculatedCorrelation(correlationValue);

			this.args.onRegressorChange(regressor);
		}
	}

	@action
	handleCorrelationRecalculate(): void {
		if (!this.isRegressorRow) {
			return;
		}

		const regressor = this.args.model as RegressorMetric;
		const forecast = this.args.forecast;

		const forecastMetricSetupData = forecast.metricSetup.metricSetupData;
		const regressorMetricSetupData = regressor.metricSetup?.metricSetupData;

		if (!forecastMetricSetupData || !regressorMetricSetupData) {
			regressor.setCalculatedCorrelation();
			regressor.isCalculatingCorrelation = false;
			return;
		}

		regressor.isCalculatingCorrelation = true;

		this.mmForecastsService
			.requestMetricCorrelation(forecastMetricSetupData, regressorMetricSetupData)
			.then((correlationResponse) => {
				regressor.setCalculatedCorrelation(correlationResponse.correlation);
			})
			.catch((_error) => {
				regressor.setCalculatedCorrelation();
			})
			.finally(() => {
				regressor.isCalculatingCorrelation = false;

				// trigger main forecast preview with updated correlation
				this.args.onRegressorChange(regressor);
			});
	}

	@action
	handleIsEnabledChange(enabled: boolean): void {
		if (this.isRegressorRow) {
			const regressor = this.args.model as RegressorMetric;

			regressor.enabled = enabled;

			this.args.onRegressorChange(regressor);
		}
	}

	@action
	openCorrelationFailurePopup(element: HTMLElement) {
		if (!this.isRegressorRow) {
			return;
		}

		const regressor = this.args.model as RegressorMetric;

		const popup = this.popupService.createContext(
			ForecastsPopupsCorrelationFailureComponent,
			element,
			{
				popupClass: 'popup-type-correlation-failure',
			},
		);

		return popup.open({
			onOk: () => {
				regressor.correlationCalculationFailed = false;
			},
		});
	}

	private handleAction(action: RegressorAction): void {
		if (!this.isRegressorRow) {
			return;
		}

		const regressor = this.args.model as RegressorMetric;

		switch (action) {
			case RegressorAction.OPEN_METRIC_INSIGHTS: {
				const metricSetup = regressor.metricSetup;
				if (metricSetup) {
					// use the date range from the main forecast's historical data select
					const dateRangeFormula = this.args.forecast.historicalDataSelect?.dateRange;
					this.metricDetailsService.openMetricDetailsDrawer(
						{
							metricSetup,
							dateRange: dateRangeFormula,
						},
						'forecasts',
					);
				}
				break;
			}
			case RegressorAction.REMOVE_REGRESSOR:
				this.args.onRegressorRemove(regressor);
				break;
		}
	}

	// -------------------------------- Connect stuff --------------------------------

	get needsConnection(): boolean {
		if (this.isRegressorRow) {
			return (this.args.model as RegressorMetric).needsConnection;
		}
		return false;
	}

	get showConnectButton(): boolean {
		if (this.args.isViewingHistoricalSnapshot) {
			return false;
		}

		return this.needsConnection;
	}

	@cached
	get missingSourceTypeIds(): number[] {
		const sourceTypeIds: number[] = [];
		const ds = this.args.model.historicalDataSelect || null;
		if (!ds) {
			return sourceTypeIds;
		}
		const metric = ds.metricSetup.metric;
		if (metric && !metric.isOnSource) {
			const sourceTypeId = metric.sourceType?.id;
			if (sourceTypeId) {
				sourceTypeIds.push(sourceTypeId);
			}
		}

		return uniq(sourceTypeIds);
	}

	@cached
	get sourceTypeToConnect() {
		const sourceTypeId = +this.missingSourceTypeIds[0]!;
		return this.mmSourceTypesService.findModelById(sourceTypeId);
	}

	get isActionConnect() {
		return !this.sourceTypeToConnect?.connectedSources.length;
	}

	get connectButtonText() {
		return this.isActionConnect ? 'Connect' : 'Select';
	}

	@action
	handleConnect(): void {
		if (!this.isRegressorRow) {
			return;
		}

		const regressor = this.args.model as RegressorMetric;
		const sourceType = this.sourceTypeToConnect;

		if (!sourceType) {
			return;
		}

		if (this.isActionConnect) {
			void sourceType
				.connectNew({ origin: 'forecasts' })
				.then((result: onConnectSourceResult) => {
					if (result.isSuccess && result.source.id) {
						next(() => {
							// update the regressor's metricSetup with the new spaceAccessId
							regressor.metricSetupSpaceAccessId = +result.source.id;
							this.args.onRegressorChange(regressor);
						});
					}
				});
		} else {
			// has connected sources - open source selection popup
			const onConnect = (sourceData?: { id: string | number | null }) => {
				if (sourceData?.id) {
					regressor.metricSetupSpaceAccessId = +sourceData.id;
					this.args.onRegressorChange(regressor);
				}
			};

			this.popupService
				.createModal(SourcePopupResolveComponent, {
					popupClass: 'popup-type-resolve-transfer',
					allowStacked: true,
				})
				.open({
					model: [sourceType],
					onConnect,
				});
		}
	}

	// -------------------------------- Connect stuff end --------------------------------
	<template>
		<div
			class={{joinClasses
				(array
					"metric-column-content"
					(if this.showError "error-cell")
					(if this.isForecastRow "forecast-cell")
					(if this.isRegressorDisabled "disabled-cell")
				)
			}}
		>
			{{#if this.isForecastRow}}
				{{! Main metric row with labels }}
				<div class="main-metric-cell">
					<div class="metric-info">
						<div class="metric-header">
							{{#if this.metricIcon}}
								<img alt="Source" class="datasource-icon" src={{this.metricIcon}} />
							{{/if}}
							<ScrollableTextComponent>
								<span class="metric-name">{{this.metricName}}</span>
							</ScrollableTextComponent>
							{{#if this.isLoading}}
								<span class="metric-loading-spinner"><LoaderSvgComponent /></span>
							{{/if}}
							{{#if this.showAttentionIcon}}
								<span class="regressor-error-icon-wrapper">
									<CommonUiIconComponent
										@name="AlertFill"
										@size="sm"
										@class="regressor-error-icon"
									/>
									<ToolTipComponent @text={{this.attentionMessage}} />
								</span>
							{{/if}}
						</div>
					</div>
					<div class="metric-labels">
						<div class="actual-label">Actual</div>
						<div class="forecast-label">Forecast</div>
						<div class="compare-label">Compare</div>
					</div>
				</div>
			{{else}}
				{{! Regular metric row with correlation }}
				<div class="metric-cell">
					<div class="metric-info">
						<div class="metric-header">
							<CommonUiIconComponent @name="Connector" @size="sm" />
							{{#if this.metricIcon}}
								<img alt="Source" class="datasource-icon" src={{this.metricIcon}} />
							{{/if}}
							<ScrollableTextComponent>
								<span class="datasource-name">
									{{@model.metricSetup.metric.sourceName}}
								</span>
								&nbsp;/&nbsp;
								<span class="metric-name">
									{{@model.metricSetup.selectedMetricTitle}}
								</span>
							</ScrollableTextComponent>
							{{#if this.isLoading}}
								<span class="metric-loading-spinner">
									<LoaderSvgComponent />
								</span>
							{{/if}}
							{{! Connect button for regressor metrics that need data source connection }}
							{{#if this.showConnectButton}}
								<SourceConnectButtonComponent
									@sourceType={{this.sourceTypeToConnect}}
									@onConnect={{this.handleConnect}}
									@text={{this.connectButtonText}}
									@theme="green"
									@size="tiny"
								/>
							{{else if this.showAttentionIcon}}
								<span class="regressor-error-icon-wrapper">
									<CommonUiIconComponent
										@name="AlertFill"
										@size="sm"
										@class="regressor-error-icon"
									/>
									<ToolTipComponent @text={{this.attentionMessage}} />
								</span>
							{{/if}}
						</div>
						<div class="forecast-methodology">
							<ForecastsSettingsRegressorMethodologyComponent
								@selectedMethodology={{@model.methodology}}
								@onChange={{this.changeMethodology}}
								@isDisabled={{this.isDisabled}}
								@timeframe={{@forecast.timeframe}}
								@endDate={{@forecast.endDate}}
							/>
						</div>
					</div>
					<div class="regressor-controls">
						<div class="regressor-toggle-row">
							<FormCheckboxSwitchComponent
								@name="regressor-enabled"
								@selected={{this.isRegressorEnabled}}
								@onChange={{this.handleIsEnabledChange}}
								@isDisabled={{this.isDisabled}}
								@size="small"
							/>
							<a
								role="button"
								class="regressor-more-options"
								{{on "click" this.openRegressorOptions}}
							>
								<CommonUiIconComponent @name="Ellipsis" @size="sm" />
							</a>
						</div>

						<div>
							{{#if this.hasCorrelationCalculationFailed}}
								<span
									class="correlation-failure-icon-wrapper"
									{{onHoverPopup this.openCorrelationFailurePopup}}
								>
									<CommonUiIconComponent
										@name="AlertFill"
										@size="sm"
										@class="correlation-failure-icon"
									/>
								</span>
							{{/if}}
							<ForecastsDataCorrelationBadgeComponent
								@correlationValue={{this.correlationValue}}
								@onChange={{this.handleCorrelationChange}}
								@onRecalculate={{this.handleCorrelationRecalculate}}
								@isDisabled={{this.isDisabled}}
								@isCalculating={{this.isCalculatingCorrelation}}
							/>
						</div>
					</div>
				</div>
			{{/if}}
		</div>
	</template>
}
